(function () {
  'use strict';

  var AUTOPLAY_DELAY = 10000;
  var DESKTOP_BREAKPOINT = 1025;
  var galleryStates = new WeakMap();
  var resizeTimer = null;

  function teardownGallery(gallery) {
    var state = galleryStates.get(gallery);
    if (!state) return;

    if (state.stopAutoplay) state.stopAutoplay();
    if (state.classObserver) state.classObserver.disconnect();
    if (state.abortController) state.abortController.abort();
    if (state.list) state.list.classList.remove('is-dragging');
    if (state.dotsRoot) {
      state.dotsRoot.innerHTML = '';
      state.dotsRoot.style.display = '';
    }

    galleryStates.delete(gallery);
    gallery.dataset.autoplayReady = 'false';
  }

  function initGallery(gallery) {
    if (!gallery) return;

    var list = gallery.querySelector('[data-product-gallery-list]');
    var dotsRoot = gallery.querySelector('[data-product-gallery-dots]');
    if (!list || !dotsRoot) return;

    var items = Array.prototype.slice.call(list.querySelectorAll('.product__media-item[data-media-id]'));
    if (items.length < 2) {
      teardownGallery(gallery);
      dotsRoot.style.display = 'none';
      return;
    }

    var mode = window.innerWidth >= DESKTOP_BREAKPOINT ? 'desktop' : 'mobile';
    var existingState = galleryStates.get(gallery);
    if (existingState && existingState.mode === mode) return;
    if (existingState) teardownGallery(gallery);

    if (mode === 'desktop') {
      dotsRoot.style.display = 'none';
      galleryStates.set(gallery, { mode: 'desktop', list: list, dotsRoot: dotsRoot });
      gallery.dataset.autoplayReady = 'true';
      return;
    }

    dotsRoot.style.display = '';
    dotsRoot.innerHTML = '';

    var activeIndex = Math.max(
      0,
      items.findIndex(function (item) {
        return item.classList.contains('is-active');
      })
    );
    var timeoutId = null;
    var dots = [];
    var isScrollSyncLocked = false;
    var abortController = new AbortController();
    var signal = abortController.signal;
    var classObserver = null;
    var dragState = {
      isPointerDown: false,
      pointerId: null,
      startX: 0,
      startScrollLeft: 0,
      hasMoved: false,
      suppressClick: false,
    };
    var DRAG_THRESHOLD = 6;

    function syncActiveIndexFromDom() {
      var idx = items.findIndex(function (item) {
        return item.classList.contains('is-active');
      });
      if (idx >= 0) activeIndex = idx;
    }

    function updateDots() {
      dots.forEach(function (dot, index) {
        var isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function setActiveByIndex(index, options) {
      if (index < 0 || index >= items.length) return;
      options = options || {};
      var previous = items[activeIndex];
      activeIndex = index;
      var target = items[index];

      if (previous && previous !== target) {
        previous.classList.remove('is-active');
        previous.classList.remove('is-leaving');
        previous.classList.remove('is-fade-out');
      }

      items.forEach(function (item) {
        if (item === target) return;
        item.classList.remove('is-leaving');
        item.classList.remove('is-fade-out');
      });
      target.classList.add('is-active');

      if (options.scroll !== false) {
        isScrollSyncLocked = true;
        target.scrollIntoView({
          behavior: options.behavior || 'smooth',
          block: 'nearest',
          inline: 'start',
        });
        window.setTimeout(function () {
          isScrollSyncLocked = false;
        }, 250);
      }

      requestAnimationFrame(function () {
        syncActiveIndexFromDom();
        updateDots();
      });
    }

    function next() {
      setActiveByIndex((activeIndex + 1) % items.length);
    }

    function stopAutoplay() {
      if (!timeoutId) return;
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    function scheduleAutoplayTick() {
      stopAutoplay();
      timeoutId = setTimeout(function () {
        next();
        scheduleAutoplayTick();
      }, AUTOPLAY_DELAY);
    }

    function startAutoplay() {
      stopAutoplay();
      if (document.hidden) return;
      scheduleAutoplayTick();
    }

    function syncActiveSlideByScroll() {
      if (isScrollSyncLocked || !items.length) return;
      var scrollLeft = list.scrollLeft;
      var nearestIndex = 0;
      var nearestDistance = Infinity;

      items.forEach(function (item, index) {
        var distance = Math.abs(item.offsetLeft - scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex === activeIndex) return;
      setActiveByIndex(nearestIndex, { scroll: false });
    }

    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragState.isPointerDown = true;
      dragState.pointerId = event.pointerId;
      dragState.startX = event.clientX;
      dragState.startScrollLeft = list.scrollLeft;
      dragState.hasMoved = false;
      dragState.suppressClick = false;
      stopAutoplay();
      list.classList.add('is-dragging');
      if (list.setPointerCapture && event.pointerId != null) {
        list.setPointerCapture(event.pointerId);
      }
    }

    function onPointerMove(event) {
      if (!dragState.isPointerDown) return;
      if (dragState.pointerId != null && event.pointerId !== dragState.pointerId) return;

      var deltaX = event.clientX - dragState.startX;
      if (!dragState.hasMoved && Math.abs(deltaX) > DRAG_THRESHOLD) {
        dragState.hasMoved = true;
      }

      if (!dragState.hasMoved) return;
      event.preventDefault();
      list.scrollLeft = dragState.startScrollLeft - deltaX;
      dragState.suppressClick = true;
    }

    function onPointerUp(event) {
      if (!dragState.isPointerDown) return;
      if (dragState.pointerId != null && event.pointerId !== dragState.pointerId) return;

      dragState.isPointerDown = false;
      dragState.pointerId = null;
      list.classList.remove('is-dragging');
      if (list.releasePointerCapture && event.pointerId != null && list.hasPointerCapture(event.pointerId)) {
        list.releasePointerCapture(event.pointerId);
      }
      startAutoplay();
      window.setTimeout(syncActiveSlideByScroll, 100);
    }

    dots = items.map(function (_item, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-gallery-dot';
      button.setAttribute('aria-label', 'Go to slide ' + (index + 1));
      button.addEventListener(
        'click',
        function () {
          setActiveByIndex(index);
          startAutoplay();
        },
        { signal: signal }
      );
      dotsRoot.appendChild(button);
      return button;
    });

    dotsRoot.addEventListener('mouseenter', stopAutoplay, { signal: signal });
    dotsRoot.addEventListener('mouseleave', startAutoplay, { signal: signal });
    list.addEventListener('mouseenter', stopAutoplay, { signal: signal });
    list.addEventListener('mouseleave', startAutoplay, { signal: signal });
    list.addEventListener(
      'touchstart',
      function () {
        stopAutoplay();
      },
      { passive: true, signal: signal }
    );
    list.addEventListener('touchend', startAutoplay, { signal: signal });
    list.addEventListener('mousedown', stopAutoplay, { signal: signal });
    list.addEventListener('mouseup', startAutoplay, { signal: signal });
    list.addEventListener('scroll', syncActiveSlideByScroll, { passive: true, signal: signal });
    list.addEventListener('pointerdown', onPointerDown, { signal: signal });
    list.addEventListener('pointermove', onPointerMove, { signal: signal });
    list.addEventListener('pointerup', onPointerUp, { signal: signal });
    list.addEventListener('pointercancel', onPointerUp, { signal: signal });
    list.addEventListener('lostpointercapture', onPointerUp, { signal: signal });
    list.addEventListener(
      'click',
      function (event) {
        if (!dragState.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        dragState.suppressClick = false;
      },
      { capture: true, signal: signal }
    );

    document.addEventListener(
      'visibilitychange',
      function () {
        if (document.hidden) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      },
      { signal: signal }
    );

    classObserver = new MutationObserver(function () {
      syncActiveIndexFromDom();
      updateDots();
    });
    items.forEach(function (item) {
      classObserver.observe(item, { attributes: true, attributeFilter: ['class'] });
    });

    updateDots();
    setActiveByIndex(activeIndex, { behavior: 'auto' });
    startAutoplay();

    galleryStates.set(gallery, {
      mode: 'mobile',
      list: list,
      dotsRoot: dotsRoot,
      abortController: abortController,
      classObserver: classObserver,
      stopAutoplay: stopAutoplay,
    });
    gallery.dataset.autoplayReady = 'true';
  }

  function initAll() {
    document.querySelectorAll('[id^="GalleryViewer-"]').forEach(initGallery);
  }

  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      initAll();
    }, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('resize', onResize);
  document.addEventListener('shopify:section:load', initAll);
})();
